import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import apiService from '../services/apiService'
import { BookOpen, Trash2, User } from 'lucide-react'
import ConfirmModal from '../ui/ConfirmModal'

function CourseAssignmentsList() {
  const [assignments, setAssignments] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [entriesCount, setEntriesCount] = useState(12)
  const [currentPage, setCurrentPage] = useState(1)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [assignmentToDelete, setAssignmentToDelete] = useState(null)
  const api = apiService()

  useEffect(() => {
    fetchAssignments()
  }, [])

  const fetchAssignments = async () => {
    try {
      setIsLoading(true)
      const response = await api.get('courses/assignments')
      console.log('API Response:', response)
      if (response.status === 200) {
        if (response.data.success && Array.isArray(response.data.assignments)) {
          setAssignments(response.data.assignments || [])
        } else if (Array.isArray(response.data)) {
          // Handle case where response.data is directly an array
          setAssignments(response.data || [])
        } else {
          console.error('Unexpected response structure:', response.data)
          toast.error(response.data?.message || 'Failed to fetch course assignments')
        }
      } else {
        toast.error(response.data?.message || 'Failed to fetch course assignments')
      }
    } catch (error) {
      console.error('Error fetching course assignments:', error)
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to fetch course assignments'
      toast.error(errorMessage)
      console.error('Full error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleUnassign = async (assignment) => {
    try {
      const response = await api.del(`courses/${assignment.course_id}/student/${assignment.student_id}`)
      if (response.status === 200) {
        toast.success('Course unassigned successfully')
        await fetchAssignments()
        setDeleteModalOpen(false)
        setAssignmentToDelete(null)
      }
    } catch (error) {
      console.error('Error unassigning course:', error)
      toast.error(error.response?.data?.message || 'Failed to unassign course')
    }
  }

  const handleDeleteClick = (assignment) => {
    setAssignmentToDelete(assignment)
    setDeleteModalOpen(true)
  }

  const handleConfirmDelete = () => {
    if (assignmentToDelete) {
      handleUnassign(assignmentToDelete)
    }
  }

  // Filtered and paginated data
  const filteredData = assignments.filter((item) => {
    const searchLower = searchTerm.toLowerCase()
    return (
      item.student_name?.toLowerCase().includes(searchLower) ||
      item.student_email?.toLowerCase().includes(searchLower) ||
      item.course_title?.toLowerCase().includes(searchLower) ||
      item.course_description?.toLowerCase().includes(searchLower)
    )
  })

  const indexOfLastEntry = currentPage * entriesCount
  const indexOfFirstEntry = indexOfLastEntry - entriesCount
  const paginatedData = filteredData.slice(indexOfFirstEntry, indexOfLastEntry)
  const totalPages = Math.ceil(filteredData.length / entriesCount)

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber)
  }

  useEffect(() => {
    setCurrentPage(1)
  }, [entriesCount, searchTerm])

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    } catch (error) {
      return 'N/A'
    }
  }

  return (
    <section className="bg-[#FCFCFC] min-h-screen">
      <div className="bg-white border border-[#F0F0F0] rounded-[10px]">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 p-5 border-b border-[#F0F0F0]">
          <div>
            <h1 className="text-2xl font-bold text-[#030303]">Course Assignments</h1>
            <p className="text-sm text-[#535862] mt-1">
              Track which courses are assigned to which students
            </p>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 p-5 border-b border-[#F0F0F0]">
          <div className="flex-1 w-full md:w-auto">
            <div className="relative">
              <input
                type="text"
                placeholder="Search by student name, email, or course title..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 pl-10 border border-[#F0F0F0] rounded-md focus:outline-none focus:ring-2 focus:ring-[#DE3C7D] focus:border-transparent"
              />
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#535862]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-[#535862]">Show:</label>
            <select
              value={entriesCount}
              onChange={(e) => setEntriesCount(Number(e.target.value))}
              className="px-3 py-2 border border-[#F0F0F0] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#DE3C7D]"
            >
              <option value={12}>12</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-sm text-[#535862]">entries</span>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-50">
              <tr>
                <th className="text-sm text-[#535862] font-medium text-center border w-[100px] px-5 py-3.5 border-[#F0F0F0]">
                  S.No
                </th>
                <th className="text-sm text-[#535862] font-medium text-left border w-60 px-5 py-3.5 border-[#F0F0F0]">
                  Student Name
                </th>
                <th className="text-sm text-[#535862] font-medium text-left border w-[280px] px-5 py-3.5 border-[#F0F0F0]">
                  Student Email
                </th>
                <th className="text-sm text-[#535862] font-medium text-left border w-60 px-5 py-3.5 border-[#F0F0F0]">
                  Course Name
                </th>
                <th className="text-sm text-[#535862] font-medium text-left border w-[200px] px-5 py-3.5 border-[#F0F0F0]">
                  Course Description
                </th>
                <th className="text-sm text-[#535862] font-medium text-center border w-[180px] px-5 py-3.5 border-[#F0F0F0]">
                  Created At
                </th>
                <th className="text-sm text-[#535862] font-medium text-center border w-[180px] px-5 py-3.5 border-[#F0F0F0]">
                  Updated At
                </th>
                <th className="text-sm text-[#535862] font-medium text-center px-5 py-3.5 border border-[#F0F0F0]">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <tr key={index} className="border border-[#F0F0F0]">
                    <td className="text-sm text-[#030303] text-center border px-5 py-2 border-[#F0F0F0]">
                      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                    </td>
                    <td className="text-sm text-[#030303] border px-5 py-2 border-[#F0F0F0]">
                      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                    </td>
                    <td className="text-sm text-[#030303] border px-5 py-2 border-[#F0F0F0]">
                      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                    </td>
                    <td className="text-sm text-[#030303] border px-5 py-2 border-[#F0F0F0]">
                      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                    </td>
                    <td className="text-sm text-[#030303] border px-5 py-2 border-[#F0F0F0]">
                      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                    </td>
                    <td className="text-sm text-[#030303] text-center border px-5 py-2 border-[#F0F0F0]">
                      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                    </td>
                    <td className="text-sm text-[#030303] text-center border px-5 py-2 border-[#F0F0F0]">
                      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                    </td>
                    <td className="px-5 py-2">
                      <div className="h-4 bg-gray-200 rounded animate-pulse w-16 mx-auto"></div>
                    </td>
                  </tr>
                ))
              ) : paginatedData.length > 0 ? (
                paginatedData.map((assignment, index) => (
                  <tr
                    key={assignment._id}
                    className="border border-[#F0F0F0] hover:bg-neutral-50 transition-colors"
                  >
                    <td className="text-sm text-[#030303] text-center border px-5 py-2 border-[#F0F0F0]">
                      {String((currentPage - 1) * entriesCount + index + 1).padStart(2, '0')}
                    </td>
                    <td className="text-sm text-[#030303] border px-5 py-2 border-[#F0F0F0]">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-[#535862]" />
                        <span className="font-medium">{assignment.student_name || 'N/A'}</span>
                      </div>
                    </td>
                    <td className="text-sm text-[#030303] border px-5 py-2 border-[#F0F0F0]">
                      {assignment.student_email || 'N/A'}
                    </td>
                    <td className="text-sm text-[#030303] border px-5 py-2 border-[#F0F0F0]">
                      <div className="flex items-center gap-2">
                        {assignment.course_thumbnail ? (
                          <img
                            src={
                              assignment.course_thumbnail.startsWith('/uploads/')
                                ? `${process.env.REACT_APP_BASE_URL?.replace(/\/$/, '')}${assignment.course_thumbnail}`
                                : assignment.course_thumbnail
                            }
                            alt={assignment.course_title}
                            className="w-8 h-8 object-cover rounded"
                            onError={(e) => {
                              e.target.style.display = 'none'
                            }}
                          />
                        ) : (
                          <div className="w-8 h-8 bg-gradient-to-br from-[#DE3C7D] to-[#c8356f] rounded flex items-center justify-center">
                            <BookOpen className="w-4 h-4 text-white" />
                          </div>
                        )}
                        <span className="font-medium">{assignment.course_title || 'N/A'}</span>
                      </div>
                    </td>
                    <td className="text-sm text-[#535862] border px-5 py-2 border-[#F0F0F0]">
                      <div className="max-w-[200px] truncate" title={assignment.course_description}>
                        {assignment.course_description || 'No description'}
                      </div>
                    </td>
                    <td className="text-sm text-[#030303] text-center border px-5 py-2 border-[#F0F0F0]">
                      {formatDate(assignment.createdAt)}
                    </td>
                    <td className="text-sm text-[#030303] text-center border px-5 py-2 border-[#F0F0F0]">
                      {formatDate(assignment.updatedAt)}
                    </td>
                    <td className="px-5 py-2">
                      <button
                        onClick={() => handleDeleteClick(assignment)}
                        className="flex items-center gap-2 text-red-600 hover:text-red-700 transition-colors mx-auto"
                        title="Unassign course"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>Unassign</span>
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-[#535862]">
                    <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <p>No course assignments found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!isLoading && totalPages > 1 && (
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 p-5 border-t border-[#F0F0F0]">
            <div className="text-sm text-[#535862]">
              Showing {indexOfFirstEntry + 1} to{' '}
              {Math.min(indexOfLastEntry, filteredData.length)} of{' '}
              {filteredData.length} assignments
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 border border-[#F0F0F0] rounded-md text-sm text-[#535862] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-50 transition-colors"
              >
                Previous
              </button>
              <span className="px-4 py-2 text-sm text-[#030303]">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border border-[#F0F0F0] rounded-md text-sm text-[#535862] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-50 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false)
          setAssignmentToDelete(null)
        }}
        onConfirm={handleConfirmDelete}
        title="Unassign Course"
        message={
          assignmentToDelete
            ? `Are you sure you want to unassign "${assignmentToDelete.course_title}" from "${assignmentToDelete.student_name}"? This action cannot be undone.`
            : 'Are you sure you want to unassign this course?'
        }
      />
    </section>
  )
}

export default CourseAssignmentsList

